import React from 'react';
import type { RecoveryScenarios } from '@/types';
import { formatCurrency } from '@/utils/searchValidation';
import { cn } from '@/lib/utils';

interface RecoveryMatrixProps {
  scenarios: RecoveryScenarios;
  amountToCollect: number;
  className?: string;
}

export const RecoveryMatrix: React.FC<RecoveryMatrixProps> = ({ scenarios, amountToCollect, className }) => {
  // Guard against undefined scenarios
  if (!scenarios || !scenarios.optimized || !scenarios.low || !scenarios.high) {
    return null;
  }

  const renderScenarioTable = (title: string, data: RecoveryScenarios['optimized'], level: 'optimized' | 'low' | 'high') => {
    // Additional guard for empty data
    if (!data || data.length === 0) {
      return null;
    }
    const getLevelConfig = (level: string) => {
      switch (level) {
        case 'optimized':
          return {
            label: 'Optimized',
            color: 'bg-blue-50',
            borderColor: 'border-blue-200',
            headerColor: 'bg-slate-100',
          };
        case 'low':
          return {
            label: 'Low',
            color: 'bg-slate-50',
            borderColor: 'border-slate-200',
            headerColor: 'bg-slate-100',
          };
        case 'high':
          return {
            label: 'High',
            color: 'bg-indigo-50',
            borderColor: 'border-indigo-200',
            headerColor: 'bg-indigo-100',
          };
        default:
          return {
            label: '',
            color: 'bg-gray-50',
            borderColor: 'border-gray-200',
            headerColor: 'bg-gray-100',
          };
      }
    };

    const config = getLevelConfig(level);

    return (
      <div className={cn('rounded-lg border overflow-hidden', config.borderColor)}>
        <div className={cn('px-3 py-1.5 border-b border-slate-200', config.headerColor)}>
          <h3 className="font-semibold text-slate-900 text-xs">{title}</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={cn('border-b border-slate-200', config.color)}>
                <th className="px-3 py-1.5 text-left font-medium text-slate-700 text-xs">
                  Recovery Scenario
                </th>
                <th className="px-3 py-1.5 text-right font-medium text-slate-700 text-xs">$ Payment</th>
                <th className="px-3 py-1.5 text-center font-medium text-slate-700 text-xs">
                  Repayment Term
                </th>
                <th className="px-3 py-1.5 text-right font-medium text-slate-700 text-xs">
                  Total $ Recovered
                </th>
                <th className="px-3 py-1.5 text-right font-medium text-slate-700 text-xs">
                  Pr (Recovery)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data.map((scenario, idx) => (
                <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-1.5 font-medium text-slate-900 text-sm">{idx + 1}</td>
                  <td className="px-3 py-1.5 text-right font-medium text-slate-900 text-sm whitespace-nowrap">
                    {formatCurrency(scenario.payment)}
                  </td>
                  <td className="px-3 py-1.5 text-center text-slate-700 text-sm">{scenario.repaymentTerm}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-green-600 text-sm whitespace-nowrap">
                    {formatCurrency(scenario.totalRecovered)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-slate-700 text-sm">{scenario.probabilityRecovery.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div>
        <h2 className="text-base font-semibold text-slate-900 mb-1">Recovery Scenarios</h2>
        <p className="text-xs text-slate-600">
          AI-powered recovery analysis based on collectability score and debt amount of {formatCurrency(amountToCollect)}
        </p>
      </div>

      {renderScenarioTable('Optimized', scenarios.optimized, 'optimized')}
      {renderScenarioTable('Low', scenarios.low, 'low')}
      {renderScenarioTable('High', scenarios.high, 'high')}
    </div>
  );
};

